import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";
import type { EntryViewT } from "@/types/database/entries";
import dayjs from "dayjs";
import { Area, AreaChart, XAxis } from "recharts";
import {
	convertEntriesToChartConfig,
	convertEntriesToChartData,
} from "./utils";

export function Eq5dChart({
	entries,
	formId,
	disableAnimations = false,
}: {
	entries: EntryViewT[];
	formId: string;
	disableAnimations?: boolean;
}) {
	const chartConfig = convertEntriesToChartConfig(entries, formId);
	const chartData = convertEntriesToChartData(entries, formId);

	// Transform the data to match the expected format
	const transformedData = chartData.map((item) => ({
		...item,
		date: new Date(item.date).getTime(), // Convert to timestamp
		"EQ-5D-5L Total Score": item["EQ-5D-5L Total Score"] ?? 0,
		timing_label: item.timing_label ?? item.timing ?? "No timing", // Ensure timing_label is preserved
	}));

	return (
		<ChartContainer config={chartConfig}>
			<AreaChart accessibilityLayer data={transformedData}>
				<XAxis
					dataKey="date"
					tickLine={false}
					tickMargin={10}
					axisLine={false}
					tickFormatter={(value) => {
						return dayjs(value).format("DD MMM");
					}}
				/>
				<Area
					type="natural"
					dataKey="EQ-5D-5L Total Score"
					stroke="hsl(var(--chart-2))"
					fill="hsl(var(--chart-2))"
					strokeWidth={2}
					isAnimationActive={!disableAnimations}
				/>
				<ChartTooltip
					content={
						<ChartTooltipContent
							hideLabel
							className="p-3 w-[200px]"
							formatter={(value, name, item) => (
								<>
									<div className="mb-2 pb-2 border-b">
										<div className="font-medium text-slate-900 text-sm">
											{item.payload.timing_label}
										</div>
										<div className="mt-1 text-slate-600 text-xs">
											{dayjs(item.payload.date).format("DD MMM YYYY")}
										</div>
									</div>
									<div className="flex justify-between items-center text-slate-800">
										<span className="font-medium text-sm">
											{chartConfig[name as keyof typeof chartConfig]?.label ||
												name}
										</span>
										<div className="bg-indigo-100 px-2 py-0.5 rounded-sm font-mono font-medium tabular-nums text-foreground">
											{value}
										</div>
									</div>
								</>
							)}
						/>
					}
					cursor={false}
				/>
			</AreaChart>
		</ChartContainer>
	);
}
